/* ===========================================================================
 *  Van een foto een scan maken
 *
 *  Casper: "kan je daar niet iets beters voor gebruiken? zodat je altijd een
 *  document scanned, meeste smartphones hebben dit al, maar dit voorkomt dat
 *  je een onleesbaar iets maakt."
 *
 *  Een webpagina kan de scanner van iOS of Android niet aanroepen. Dit doet
 *  wat die doen, op het toestel zelf en zonder bibliotheek:
 *
 *    1. vindHoeken()     het papier in de foto vinden (licht op donker)
 *    2. rechtTrekken()   de vier hoeken naar een rechthoek: een schuine
 *                        foto wordt een recht vel
 *    3. alsDocument()    de schaduw eruit: wit papier, donkere tekst
 *    4. keurScan()       is het leesbaar? wazig, te donker, te klein
 *
 *  Alles hier werkt op kale pixelrijen (Uint8ClampedArray, RGBA), niet op
 *  een canvas. Dan is het na te rekenen zonder browser (dev/scan-
 *  nakijken.ts), en het scherm doet alleen het tekenen.
 *
 *  Wat dit NIET is: tekstherkenning. Of er de goede factuur op staat, ziet
 *  de AI-assistent daarna. Dit zorgt dat hij iets te lezen krijgt.
 * =========================================================================== */

export interface Beeld {
  data: Uint8ClampedArray<ArrayBuffer>
  width: number
  height: number
}

export interface Punt { x: number; y: number }

/** Linksboven, rechtsboven, rechtsonder, linksonder. */
export type Hoeken = [Punt, Punt, Punt, Punt]

/* ------------------------------------------------------------------ *
 *  Grijswaarden
 * ------------------------------------------------------------------ */

export function grijs(b: Beeld): Uint8ClampedArray {
  const uit = new Uint8ClampedArray(b.width * b.height)
  const d = b.data
  for (let i = 0, j = 0; j < uit.length; i += 4, j++) {
    uit[j] = (d[i]! * 77 + d[i + 1]! * 150 + d[i + 2]! * 29) >> 8
  }
  return uit
}

/** Een kastvervaging, twee keer (horizontaal en verticaal). Snel genoeg voor een telefoon. */
export function vervaag(g: Uint8ClampedArray, w: number, h: number, straal: number): Uint8ClampedArray {
  if (straal < 1) return g.slice()
  const tmp = new Float32Array(w * h)
  const uit = new Uint8ClampedArray(w * h)
  for (let y = 0; y < h; y++) {
    let som = 0
    const r = y * w
    for (let x = -straal; x <= straal; x++) som += g[r + Math.min(w - 1, Math.max(0, x))]!
    for (let x = 0; x < w; x++) {
      tmp[r + x] = som / (2 * straal + 1)
      som += g[r + Math.min(w - 1, x + straal + 1)]! - g[r + Math.max(0, x - straal)]!
    }
  }
  for (let x = 0; x < w; x++) {
    let som = 0
    for (let y = -straal; y <= straal; y++) som += tmp[Math.min(h - 1, Math.max(0, y)) * w + x]!
    for (let y = 0; y < h; y++) {
      uit[y * w + x] = som / (2 * straal + 1)
      som += tmp[Math.min(h - 1, y + straal + 1) * w + x]! - tmp[Math.max(0, y - straal) * w + x]!
    }
  }
  return uit
}

/** De drempel van Otsu: waar licht en donker het best uit elkaar liggen. */
export function otsu(g: Uint8ClampedArray): number {
  const hist = new Float64Array(256)
  for (let i = 0; i < g.length; i++) hist[g[i]!]++
  const n = g.length
  let somAlles = 0
  for (let i = 0; i < 256; i++) somAlles += i * hist[i]!
  let somAchter = 0
  let gewichtAchter = 0
  let beste = 0
  let drempel = 127
  for (let t = 0; t < 256; t++) {
    gewichtAchter += hist[t]!
    if (gewichtAchter === 0) continue
    const gewichtVoor = n - gewichtAchter
    if (gewichtVoor === 0) break
    somAchter += t * hist[t]!
    const mAchter = somAchter / gewichtAchter
    const mVoor = (somAlles - somAchter) / gewichtVoor
    const tussen = gewichtAchter * gewichtVoor * (mAchter - mVoor) ** 2
    if (tussen > beste) { beste = tussen; drempel = t }
  }
  return drempel
}

/* ------------------------------------------------------------------ *
 *  1. Het papier vinden
 *
 *  Papier is lichter dan de tafel eronder. Dus: vervagen (zodat de tekst
 *  geen gaten in het vel slaat), splitsen op de drempel van Otsu, het
 *  grootste lichte stuk dat niet de rand van de foto IS, en daarvan de vier
 *  uiterste punten. Simpel en voor het gewone geval -- een vel op een
 *  bureau -- goed. Gaat het mis (wit papier op een wit bureau), dan zijn de
 *  hoeken met de vinger te verslepen; daarom geeft dit null in plaats van
 *  een gok die er overtuigend uitziet.
 * ------------------------------------------------------------------ */

export function vindHoeken(b: Beeld): Hoeken | null {
  const { width: w, height: h } = b
  const g = vervaag(grijs(b), w, h, Math.max(2, Math.round(Math.min(w, h) / 120)))
  const t = otsu(g)

  const licht = new Uint8Array(w * h)
  for (let i = 0; i < licht.length; i++) licht[i] = g[i]! > t ? 1 : 0

  /* Het grootste lichte stuk, vier-verbonden. */
  const label = new Int32Array(w * h)
  const stapel = new Int32Array(w * h)
  let besteLabel = 0
  let besteGrootte = 0
  let volgende = 1
  for (let start = 0; start < licht.length; start++) {
    if (!licht[start] || label[start]) continue
    let top = 0
    let grootte = 0
    stapel[top++] = start
    label[start] = volgende
    while (top > 0) {
      const p = stapel[--top]!
      grootte++
      const x = p % w
      const y = (p - x) / w
      const buren = [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, y > 0 ? p - w : -1, y < h - 1 ? p + w : -1]
      for (const q of buren) {
        if (q >= 0 && licht[q] && !label[q]) { label[q] = volgende; stapel[top++] = q }
      }
    }
    if (grootte > besteGrootte) { besteGrootte = grootte; besteLabel = volgende }
    volgende++
  }

  /* Het hele beeld licht, of bijna niets: dan is er geen vel te onderscheiden. */
  if (besteGrootte < w * h * 0.12 || besteGrootte > w * h * 0.97) return null

  let tl = { x: 0, y: 0, s: Infinity }
  let br = { x: 0, y: 0, s: -Infinity }
  let tr = { x: 0, y: 0, s: -Infinity }
  let bl = { x: 0, y: 0, s: Infinity }
  for (let p = 0; p < label.length; p++) {
    if (label[p] !== besteLabel) continue
    const x = p % w
    const y = (p - x) / w
    const som = x + y
    const verschil = x - y
    if (som < tl.s) tl = { x, y, s: som }
    if (som > br.s) br = { x, y, s: som }
    if (verschil > tr.s) tr = { x, y, s: verschil }
    if (verschil < bl.s) bl = { x, y, s: verschil }
  }
  const hoeken: Hoeken = [{ x: tl.x, y: tl.y }, { x: tr.x, y: tr.y }, { x: br.x, y: br.y }, { x: bl.x, y: bl.y }]
  if (!isBolleVierhoek(hoeken) || oppervlak(hoeken) < w * h * 0.12) return null
  return hoeken
}

export function oppervlak(q: Hoeken): number {
  let s = 0
  for (let i = 0; i < 4; i++) {
    const a = q[i]!
    const b = q[(i + 1) % 4]!
    s += a.x * b.y - b.x * a.y
  }
  return Math.abs(s) / 2
}

/** Vier punten in volgorde die samen een bolle vierhoek vormen (geen strik). */
export function isBolleVierhoek(q: Hoeken): boolean {
  let teken = 0
  for (let i = 0; i < 4; i++) {
    const a = q[i]!
    const b = q[(i + 1) % 4]!
    const c = q[(i + 2) % 4]!
    const kruis = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x)
    if (Math.abs(kruis) < 1e-9) return false
    const s = Math.sign(kruis)
    if (teken === 0) teken = s
    else if (s !== teken) return false
  }
  return true
}

/**
 * Een gevonden kader een fractie naar binnen: de rand van het vel is in de
 * vervaagde zoekversie een paar pixels breder dan echt, en een streepje
 * tafel langs de scan staat slordig en leest de AI-assistent als een lijn.
 */
export function naarBinnen(q: Hoeken, fractie = 0.008): Hoeken {
  const mx = (q[0].x + q[1].x + q[2].x + q[3].x) / 4
  const my = (q[0].y + q[1].y + q[2].y + q[3].y) / 4
  return q.map((p) => ({ x: p.x + (mx - p.x) * fractie * 2, y: p.y + (my - p.y) * fractie * 2 })) as Hoeken
}

/** Een rand van 6% naar binnen: wat je ziet als er niets gevonden is. */
export function standaardHoeken(w: number, h: number): Hoeken {
  const mx = w * 0.06
  const my = h * 0.06
  return [{ x: mx, y: my }, { x: w - mx, y: my }, { x: w - mx, y: h - my }, { x: mx, y: h - my }]
}

/* ------------------------------------------------------------------ *
 *  2. Rechttrekken
 *
 *  Een homografie: de afbeelding die de vier hoeken van de rechthoek op de
 *  vier hoeken in de foto legt. Voor elk punt van de scan zoeken we zo op
 *  waar het in de foto stond, en mengen we de vier pixels eromheen.
 * ------------------------------------------------------------------ */

/** De 3x3-matrix (als 9 getallen, h33 = 1) die `van` op `naar` afbeeldt. */
export function homografie(van: Hoeken, naar: Hoeken): number[] {
  const A: number[][] = []
  const B: number[] = []
  for (let i = 0; i < 4; i++) {
    const { x, y } = van[i]!
    const { x: u, y: v } = naar[i]!
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); B.push(u)
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); B.push(v)
  }
  /* Gauss met spilkeuze; acht onbekenden. */
  for (let k = 0; k < 8; k++) {
    let spil = k
    for (let r = k + 1; r < 8; r++) if (Math.abs(A[r]![k]!) > Math.abs(A[spil]![k]!)) spil = r
    ;[A[k], A[spil]] = [A[spil]!, A[k]!]
    ;[B[k], B[spil]] = [B[spil]!, B[k]!]
    const p = A[k]![k]!
    if (Math.abs(p) < 1e-12) throw new Error('De hoeken liggen op één lijn.')
    for (let r = k + 1; r < 8; r++) {
      const f = A[r]![k]! / p
      for (let c = k; c < 8; c++) A[r]![c]! -= f * A[k]![c]!
      B[r]! -= f * B[k]!
    }
  }
  const h = new Array<number>(8).fill(0)
  for (let k = 7; k >= 0; k--) {
    let s = B[k]!
    for (let c = k + 1; c < 8; c++) s -= A[k]![c]! * h[c]!
    h[k] = s / A[k]![k]!
  }
  return [...h, 1]
}

export function pasToe(H: number[], p: Punt): Punt {
  const n = H[6]! * p.x + H[7]! * p.y + H[8]!
  return { x: (H[0]! * p.x + H[1]! * p.y + H[2]!) / n, y: (H[3]! * p.x + H[4]! * p.y + H[5]!) / n }
}

const afstand = (a: Punt, b: Punt) => Math.hypot(a.x - b.x, a.y - b.y)

/** Hoe groot de scan wordt: de langste overstaande zijden, met een plafond. */
export function scanMaat(q: Hoeken, maxZijde = 2400): { breed: number; hoog: number } {
  const breed = Math.max(afstand(q[0], q[1]), afstand(q[3], q[2]))
  const hoog = Math.max(afstand(q[0], q[3]), afstand(q[1], q[2]))
  const f = Math.min(1, maxZijde / Math.max(breed, hoog))
  return { breed: Math.max(1, Math.round(breed * f)), hoog: Math.max(1, Math.round(hoog * f)) }
}

export function rechtTrekken(bron: Beeld, q: Hoeken, maxZijde = 2400): Beeld {
  const { breed, hoog } = scanMaat(q, maxZijde)
  const doel: Hoeken = [{ x: 0, y: 0 }, { x: breed - 1, y: 0 }, { x: breed - 1, y: hoog - 1 }, { x: 0, y: hoog - 1 }]
  const H = homografie(doel, q)
  const uit = new Uint8ClampedArray(breed * hoog * 4)
  const { data: d, width: bw, height: bh } = bron
  for (let y = 0; y < hoog; y++) {
    for (let x = 0; x < breed; x++) {
      const n = H[6]! * x + H[7]! * y + H[8]!
      let u = (H[0]! * x + H[1]! * y + H[2]!) / n
      let v = (H[3]! * x + H[4]! * y + H[5]!) / n
      u = Math.min(bw - 1.001, Math.max(0, u))
      v = Math.min(bh - 1.001, Math.max(0, v))
      const x0 = u | 0
      const y0 = v | 0
      const fx = u - x0
      const fy = v - y0
      const i00 = (y0 * bw + x0) * 4
      const i10 = i00 + 4
      const i01 = i00 + bw * 4
      const i11 = i01 + 4
      const o = (y * breed + x) * 4
      for (let c = 0; c < 3; c++) {
        const boven = d[i00 + c]! + (d[i10 + c]! - d[i00 + c]!) * fx
        const onder = d[i01 + c]! + (d[i11 + c]! - d[i01 + c]!) * fx
        uit[o + c] = boven + (onder - boven) * fy
      }
      uit[o + 3] = 255
    }
  }
  return { data: uit, width: breed, height: hoog }
}

/* ------------------------------------------------------------------ *
 *  3. Als document: de schaduw eruit
 *
 *  Het papier is niet overal even licht (een hand, een lamp, een kreukel).
 *  Elke pixel delen door hoe licht het papier DAAR is -- een sterk vervaagde
 *  versie van het beeld -- maakt het papier overal wit en laat de tekst
 *  staan. Daarna wat contrast. Grijs, en dat is met opzet: een stempel of
 *  een handtekening in blauw blijft zichtbaar, wat bij echt zwart-wit
 *  verdwijnt.
 * ------------------------------------------------------------------ */

export function alsDocument(b: Beeld): Beeld {
  const { width: w, height: h } = b
  const g = grijs(b)
  /* De achtergrond: kleiner rekenen en dan pas vervagen, anders is dit traag. */
  const stap = Math.max(1, Math.round(Math.max(w, h) / 400))
  const kw = Math.ceil(w / stap)
  const kh = Math.ceil(h / stap)
  const klein = new Uint8ClampedArray(kw * kh)
  for (let y = 0; y < kh; y++) {
    for (let x = 0; x < kw; x++) {
      /* Het lichtste punt in het blokje: dat is papier, niet een letter. */
      let max = 0
      for (let yy = y * stap; yy < Math.min(h, (y + 1) * stap); yy++) {
        for (let xx = x * stap; xx < Math.min(w, (x + 1) * stap); xx++) max = Math.max(max, g[yy * w + xx]!)
      }
      klein[y * kw + x] = max
    }
  }
  const achter = vervaag(vervaag(klein, kw, kh, 6), kw, kh, 6)

  /*
   * Een groot donker vlak (een logo, een zwarte balk) is geen schaduw.
   * Zonder ondergrens zou het als "papier" gelden en grijs worden gebleekt.
   * Het papier is hoogstens 45% donkerder dan het lichtste papier op het
   * vel; wat donkerder is, is inkt. (Het 90e percentiel van de achtergrond
   * is het lichtste papier, zonder de enkele overbelichte pixel.)
   */
  const gesorteerd = Uint8ClampedArray.from(achter).sort()
  const papier = gesorteerd[Math.floor(gesorteerd.length * 0.9)] ?? 255
  const ondergrens = Math.max(24, papier * 0.55)

  const uit = new Uint8ClampedArray(w * h * 4)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = Math.max(ondergrens, achter[Math.min(kh - 1, (y / stap) | 0) * kw + Math.min(kw - 1, (x / stap) | 0)]!)
      let v = g[y * w + x]! / a
      v = (v - 0.25) / (0.92 - 0.25)
      v = v <= 0 ? 0 : v >= 1 ? 1 : v ** 1.4
      const o = (y * w + x) * 4
      uit[o] = uit[o + 1] = uit[o + 2] = v * 255
      uit[o + 3] = 255
    }
  }
  return { data: uit, width: w, height: h }
}

/* ------------------------------------------------------------------ *
 *  4. Is het leesbaar?
 *
 *  Scherpte: de spreiding van de Laplace-operator -- veel scherpe overgangen
 *  (letters) geeft een hoog getal, een bewogen foto een laag. Gemeten op een
 *  vaste breedte, zodat het getal niet afhangt van de camera.
 *
 *  Het zijn WAARSCHUWINGEN, geen weigering. Een drempel die op een
 *  testfoto klopt, kan op een kassabon met weinig tekst te streng zijn; dan
 *  moet "Toch gebruiken" kunnen. Maar je ziet het vóór het versturen, en
 *  niet een week later in de rij bij Valideren.
 * ------------------------------------------------------------------ */

export const MIN_SCHERPTE = 35
export const MIN_HELDERHEID = 70
export const MIN_KORTE_ZIJDE = 900

export function scherpte(g: Uint8ClampedArray, w: number, h: number): number {
  let som = 0
  let somKw = 0
  let n = 0
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x
      const l = g[i - 1]! + g[i + 1]! + g[i - w]! + g[i + w]! - 4 * g[i]!
      som += l
      somKw += l * l
      n++
    }
  }
  if (!n) return 0
  const m = som / n
  return somKw / n - m * m
}

/** Op een vaste breedte (1000 px), met dichtstbijzijnde pixels: snel, en het meet wat er is. */
export function opBreedte(b: Beeld, breedte = 1000): { g: Uint8ClampedArray; w: number; h: number } {
  const f = Math.min(1, breedte / b.width)
  const w = Math.max(1, Math.round(b.width * f))
  const h = Math.max(1, Math.round(b.height * f))
  const bron = grijs(b)
  const g = new Uint8ClampedArray(w * h)
  for (let y = 0; y < h; y++) {
    const by = Math.min(b.height - 1, Math.round(y / f))
    for (let x = 0; x < w; x++) g[y * w + x] = bron[by * b.width + Math.min(b.width - 1, Math.round(x / f))]!
  }
  return { g, w, h }
}

export interface Keuring {
  scherpte: number
  helderheid: number
  problemen: string[]
}

/** Keur de rechtgetrokken scan (vóór het documentfilter: dat maakt alles helder). */
export function keurScan(b: Beeld): Keuring {
  const { g, w, h } = opBreedte(b)
  const s = scherpte(g, w, h)
  let totaal = 0
  for (let i = 0; i < g.length; i++) totaal += g[i]!
  const helderheid = totaal / g.length
  const problemen: string[] = []
  if (s < MIN_SCHERPTE) problemen.push('De scan lijkt wazig. Houd de telefoon stil en zorg dat hij scherpstelt (tik op het papier).')
  if (helderheid < MIN_HELDERHEID) problemen.push('De foto is erg donker. Zoek meer licht of zet de flitser aan.')
  if (Math.min(b.width, b.height) < MIN_KORTE_ZIJDE) problemen.push('Het papier is klein in beeld. Kom dichterbij, zodat het vel het scherm vult.')
  return { scherpte: Math.round(s), helderheid: Math.round(helderheid), problemen }
}

/** Een kwartslag met de klok mee: een vel dat dwars in beeld lag. */
export function draai90(b: Beeld): Beeld {
  const { width: w, height: h, data: d } = b
  const uit = new Uint8ClampedArray(d.length)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const van = (y * w + x) * 4
      const naar = (x * h + (h - 1 - y)) * 4
      uit[naar] = d[van]!; uit[naar + 1] = d[van + 1]!; uit[naar + 2] = d[van + 2]!; uit[naar + 3] = d[van + 3]!
    }
  }
  return { data: uit, width: h, height: w }
}
